import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import xss from 'xss';
import type { ReactNode } from 'react';
import { Column, FlexBox, GridContainer, H2, Row, Spinner, Typography } from '@vp/swan';
import { useLogger } from '@vp/ubik-context';
import { useAuthContext } from '../../auth';
import { sanitiseLocale } from '../../i18n/config';
import { useEnvContext } from '../../utils/environmentContext';
import { getDesignableProductPrice, priceFromFloat } from '../../utils/pricing';
import { bifrostEnabledLocales, logoInspirationRootUrl } from '../../utils/urls';
import LogoProductTile from './LogoProductTile';
import { useLogoProductTilesConfig } from './LogoProductTilesConfig';
import type { Logger } from '@vp/ubik-logging';
import type { LogoProductKey } from './LogoProductTilesConfig';

interface LogoProductTilesProps {
    showLogomaker?: boolean;
    showLogoContest?: boolean;
    title?: ReactNode;
    description?: ReactNode;
    trackingCategory: string;
    customProductTileCopy?: {
        [key in LogoProductKey]?: {
            title?: string;
            description?: string;
        };
    };
}

const LogoProductTiles = ({
    showLogomaker = true,
    showLogoContest = true,
    title,
    description,
    trackingCategory,
    customProductTileCopy,
}: LogoProductTilesProps) => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const { anonymousId } = useAuthContext();
    const logger: Logger = useLogger();
    // NOTE: Only Bifrost enabled locales are currently sent to /design/logo-essential.
    // All other locales are sent to 99designs eg:
    // https://99designs.de/logo-essential-and-consultation/details
    const isBifrostEnabled = bifrostEnabledLocales.includes(locale);

    const [logoEssentialListPrice, setLogoEssentialListPrice] = useState<string>('');
    const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(false);

    const logoProductTileMap = useLogoProductTilesConfig(trackingCategory, locale);

    const { ENV_API_BASE_URL } = useEnvContext();

    useEffect(() => {
        const fetchLogoEssentialPrice = async () => {
            setIsLoadingPrice(true);
            const price = await getDesignableProductPrice(
                'logoessential',
                locale,
                ENV_API_BASE_URL,
                logger,
            );
            // Once this extends beyond Bifrost enabled locales we will need to check if VAT
            // is included to determine if the taxed or untaxed price should be displayed.
            const listPrice = price ? priceFromFloat(price.list_price.taxed, locale) : '';
            setLogoEssentialListPrice(listPrice);
            setIsLoadingPrice(false);
        };

        if (isBifrostEnabled) {
            fetchLogoEssentialPrice();
        }
    }, [locale]);

    const appendTrackingParams = (key: LogoProductKey, linkHref: string) => {
        if (!anonymousId) return linkHref;
        return (key === 'logo-essential' && !isBifrostEnabled) || key === 'logo-contest'
            ? `${linkHref}?utm_source=partner&utm_medium=referral&utm_campaign=VistaLogos&vistaprint_aid=${anonymousId}`
            : linkHref;
    };

    const defaultDescription = (
        <div
            dangerouslySetInnerHTML={{
                __html: xss(
                    t('Vista Logos > Logo Product Tiles.sectionSubHeader', {
                        logoInspirationRootLink: logoInspirationRootUrl(),
                    }) as string,
                ),
            }}
        />
    );

    return (
        <>
            <H2 fontSkin="title-section">
                {title ?? t('Vista Logos > Logo Product Tiles.sectionHeader')}
            </H2>
            <Typography fontSkin="body-standard" marginBottom="between-subsections">
                {description ?? defaultDescription}
            </Typography>
            <FlexBox gap={4}>
                <GridContainer>
                    <Row>
                        {Object.entries(logoProductTileMap)
                            .filter(
                                ([key]) =>
                                    (key !== 'logomaker' || showLogomaker) &&
                                    (key !== 'logo-contest' || showLogoContest),
                            )
                            .map(([key, productTile]) => {
                                const {
                                    title,
                                    description,
                                    imageDefault,
                                    linkHref,
                                    ctaText,
                                    priceText,
                                    ctaRef,
                                } = productTile;

                                const customCopy = customProductTileCopy?.[key as LogoProductKey];
                                return (
                                    <Column key={key} span={4}>
                                        <LogoProductTile
                                            title={customCopy?.title ?? title}
                                            description={customCopy?.description ?? description}
                                            imageSrc={imageDefault.src}
                                            altText={imageDefault.altText}
                                            price={
                                                key === 'logo-essential' ? (
                                                    isLoadingPrice ? (
                                                        <Spinner
                                                            size="tiny"
                                                            accessibleText="Loading price"
                                                        />
                                                    ) : isBifrostEnabled ? (
                                                        logoEssentialListPrice
                                                    ) : (
                                                        priceText
                                                    )
                                                ) : (
                                                    priceText
                                                )
                                            }
                                            linkHref={appendTrackingParams(
                                                key as LogoProductKey,
                                                linkHref,
                                            )}
                                            ctaText={ctaText}
                                            ctaRef={ctaRef}
                                        />
                                    </Column>
                                );
                            })}
                    </Row>
                </GridContainer>
            </FlexBox>
        </>
    );
};

export default LogoProductTiles;
